import { t } from "i18next";
import { useGetDaysOff, useUpdateDaysOff } from "modules/drivers";
import { UpdateDaysOffBody } from "modules/drivers/apis/useUpdateDaysOff/updateDaysOff.types";
import moment from "moment";
import { toast } from "react-hot-toast";

const toDateString = (value: string) => moment(value).format("YYYY-MM-DD");

export const useDeliveryRules = () => {
  const { daysOff, refetch, isLoading } = useGetDaysOff();
  const update = useUpdateDaysOff(undefined, { onSuccess: () => refetch() });

  // The PATCH is atomic — every change re-sends the full weekend + holidays +
  // cutoffSchedule with only the touched field updated. missedCutoffOffsetDays
  // isn't shown in this UI, so it's echoed back unchanged.
  const baseBody = (): UpdateDaysOffBody => ({
    weekend: daysOff!.weekend,
    holidays: daysOff!.holidays.map((holiday) => ({
      id: holiday.id,
      name: holiday.name,
      from: toDateString(holiday.from),
      to: toDateString(holiday.to)
    })),
    cutoffSchedule: daysOff!.cutoffSchedule.map((row) => ({
      dayOfWeek: row.dayOfWeek,
      cutoffTime: row.cutoffTime,
      missedCutoffOffsetDays: row.missedCutoffOffsetDays
    }))
  });

  const setCutoffTime = (dayOfWeek: number, cutoffTime: string) => {
    if (!daysOff) return;
    const body = baseBody();
    update.mutate({
      ...body,
      cutoffSchedule: body.cutoffSchedule.map((row) =>
        row.dayOfWeek === dayOfWeek ? { ...row, cutoffTime } : row
      )
    });
  };

  const toggleDeliveries = (dayOfWeek: number, on: boolean) => {
    if (!daysOff) return;
    const body = baseBody();
    const weekend = { ...body.weekend };
    if (dayOfWeek === 6) weekend.isSaturdayOff = !on;
    if (dayOfWeek === 0) weekend.isSundayOff = !on;
    update.mutate({ ...body, weekend });
  };

  const addHoliday = (name: string, date: Date) => {
    if (!daysOff) return;
    const iso = moment(date).format("YYYY-MM-DD");
    const body = baseBody();
    update.mutate(
      { ...body, holidays: [...body.holidays, { name, from: iso, to: iso }] },
      { onSuccess: () => toast.success(t("Holiday added")) }
    );
  };

  const removeHoliday = (id: string) => {
    if (!daysOff) return;
    const body = baseBody();
    update.mutate({
      ...body,
      holidays: body.holidays.filter((holiday) => holiday.id !== id)
    });
  };

  return {
    daysOff,
    isLoading,
    isSaving: update.isLoading,
    setCutoffTime,
    toggleDeliveries,
    addHoliday,
    removeHoliday
  };
};
